/**
 * Error Handling Utilities
 * 
 * This file provides comprehensive error handling utilities
 * for the NovaQ Finance System.
 */

import {
  AppError,
  ApiError,
  AuthError,
  ValidationError,
  NetworkError,
  BusinessError,
  ErrorCodes,
  ErrorSeverity,
  ErrorCategory,
  ErrorContext,
  ErrorResponse,
  SuccessResponse
} from '../types/errors';

// Error factory functions
export class ErrorFactory {
  /**
   * Create an API error
   */
  static createApiError(
    status: number,
    statusText: string,
    endpoint: string,
    method: string,
    message: string,
    response?: any,
    context?: Record<string, any>
  ): ApiError {
    return {
      code: this.getApiErrorCode(status),
      message,
      timestamp: new Date(),
      status,
      statusText,
      endpoint,
      method,
      response,
      context
    };
  }

  /**
   * Create an authentication error
   */
  static createAuthError(
    type: 'token_expired' | 'token_invalid' | 'unauthorized' | 'forbidden',
    message: string,
    redirectTo?: string,
    context?: Record<string, any>
  ): AuthError {
    return {
      code: this.getAuthErrorCode(type),
      message,
      timestamp: new Date(),
      type,
      redirectTo,
      context
    };
  }

  /**
   * Create a validation error
   */
  static createValidationError(
    field: string,
    value: any,
    rule: string,
    message: string,
    expected?: any,
    context?: Record<string, any>
  ): ValidationError {
    return {
      code: ErrorCodes.VALIDATION_ERROR,
      message,
      timestamp: new Date(),
      field,
      value,
      rule,
      expected,
      context
    };
  }

  /**
   * Create a network error
   */
  static createNetworkError(
    type: 'timeout' | 'connection_failed' | 'server_unavailable',
    url: string,
    message: string,
    retryable: boolean = true,
    context?: Record<string, any>
  ): NetworkError {
    return {
      code: ErrorCodes.API_NETWORK_ERROR,
      message,
      timestamp: new Date(),
      type,
      url,
      retryable,
      context
    };
  }

  /**
   * Create a business logic error
   */
  static createBusinessError(
    category: 'customer' | 'accountant' | 'tax' | 'insurance' | 'report' | 'system',
    operation: string,
    message: string,
    severity: 'low' | 'medium' | 'high' | 'critical' = 'medium',
    context?: Record<string, any>
  ): BusinessError {
    return {
      code: ErrorCodes.OPERATION_FAILED,
      message,
      timestamp: new Date(),
      category,
      operation,
      severity,
      context
    };
  }

  /**
   * Get appropriate error code for HTTP status
   */
  private static getApiErrorCode(status: number): string {
    switch (status) {
      case 400:
        return ErrorCodes.VALIDATION_ERROR;
      case 401:
        return ErrorCodes.UNAUTHORIZED;
      case 403:
        return ErrorCodes.FORBIDDEN;
      case 404:
        return ErrorCodes.CUSTOMER_NOT_FOUND;
      case 408:
        return ErrorCodes.API_TIMEOUT;
      case 500:
      case 502:
      case 503:
      case 504:
        return ErrorCodes.API_SERVER_ERROR;
      default:
        return ErrorCodes.API_ERROR;
    }
  }

  /**
   * Get appropriate error code for auth error type
   */
  private static getAuthErrorCode(type: string): string {
    switch (type) {
      case 'token_expired':
        return ErrorCodes.TOKEN_EXPIRED;
      case 'token_invalid':
        return ErrorCodes.TOKEN_INVALID;
      case 'unauthorized':
        return ErrorCodes.UNAUTHORIZED;
      case 'forbidden':
        return ErrorCodes.FORBIDDEN;
      default:
        return ErrorCodes.UNAUTHORIZED;
    }
  }
}

// Error logger class
export class ErrorLogger {
  private static instance: ErrorLogger;
  private logs: AppError[] = [];

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /**
   * Log an error
   */
  log(error: AppError): void {
    this.logs.push(error);
    
    // Filter out routine API errors to reduce console noise
    const isRoutineError = this.isRoutineError(error);
    
    // Log to console in development (but filter routine errors)
    if (process.env.NODE_ENV === 'development' && !isRoutineError) {
      console.group(`🚨 Error: ${error.code}`);
      console.error('Message:', error.message);
      console.error('Timestamp:', error.timestamp);
      if (error.context) {
        console.error('Context:', error.context);
      }
      console.groupEnd();
    }

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      this.logToExternalService(error);
    }
  }

  /**
   * Check if error is routine and should be suppressed
   */
  private isRoutineError(error: AppError): boolean {
    // Suppress routine API call failures
    if ('url' in error && error.url) {
      return error.url.includes('/stats') || 
             error.url.includes('/customer/accountant/stats') ||
             error.url.includes('/tax-invoice/');
    }
    
    if ('endpoint' in error && error.endpoint) {
      return error.endpoint.includes('/stats') || 
             error.endpoint.includes('/customer/accountant/stats') ||
             error.endpoint.includes('/tax-invoice/');
    }

    // Suppress business operation failures for API calls
    if ('operation' in error && error.operation === 'api_call' && 
        'category' in error && error.category === 'system') {
      return true;
    }
    
    return false;
  }

  /**
   * Log info message
   */
  logInfo(message: string, context?: Record<string, any>): void {
    if (process.env.NODE_ENV === 'development') {
      // Filter out routine API calls to reduce console noise
      if ((message.includes('API Call started') || 
           message.includes('API Response received') || 
           message.includes('API Call completed successfully'))) {
        
        // Check if this is NE-KEY API call by multiple methods
        const isNeKeyCall = (
          (context && (
            context.endpoint === '/api/tax-ne-key' || 
            context.endpoint === '/tax-ne-key' ||
            (context.url && context.url.includes('/api/tax-ne-key')) ||
            (context.url && context.url.includes('/tax-ne-key')) ||
            (context.requestId && context.requestId.includes('tax-ne-key'))
          )) ||
          // Also check if message itself contains tax-ne-key
          message.includes('/api/tax-ne-key') ||
          message.includes('/tax-ne-key')
        );
        
        if (isNeKeyCall) {
          // Don't log routine NE-KEY API calls to reduce console noise
          return;
        }
      }
      console.log(`ℹ️ ${message}`, context || '');
    }
  }

  /**
   * Log warning message
   */
  logWarning(message: string, context?: Record<string, any>): void {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`⚠️ ${message}`, context || '');
    }
  }

  /**
   * Log error message
   */
  logError(error: AppError): void {
    this.log(error);
  }

  /**
   * Get all logged errors
   */
  getLogs(): AppError[] {
    return [...this.logs];
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Log to external service (placeholder)
   */
  private logToExternalService(error: AppError): void {
    // Production environment-д алдааг зөв handle хийх
    if (process.env.NODE_ENV === 'production') {
      // Production-д зөвхөн шаардлагатай алдааг л log хийх
      if (error.code === 'BUSINESS_OPERATION_FAILED' && error.message === 'Unknown error occurred') {
        console.warn('⚠️ API call failed - this might be a temporary issue:', {
          code: error.code,
          message: 'API service temporarily unavailable',
          timestamp: error.timestamp
        });
        return;
      }
    }
    
    // TODO: Implement external logging service integration
    // Examples: Sentry, LogRocket, DataDog, etc.
    // For now, just log to console in production
    console.error('Production Error:', {
      code: error.code,
      message: error.message,
      timestamp: error.timestamp,
      category: 'system',
      operation: 'api_call',
      severity: 'high',
      context: {
        requestId: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        error: error
      }
    });
  }
}

// Error handler class
export class ErrorHandler {
  private static instance: ErrorHandler;
  private logger: ErrorLogger;

  constructor() {
    this.logger = ErrorLogger.getInstance();
  }

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Handle any error
   */
  handle(error: AppError): void {
    this.logger.log(error);
    
    // Handle different error types
    if (this.isApiError(error)) {
      this.handleApiError(error);
    } else if (this.isAuthError(error)) {
      this.handleAuthError(error);
    } else if (this.isValidationError(error)) {
      this.handleValidationError(error);
    } else if (this.isNetworkError(error)) {
      this.handleNetworkError(error);
    } else if (this.isBusinessError(error)) {
      this.handleBusinessError(error);
    } else {
      this.handleUnknownError(error);
    }
  }

  /**
   * Handle API errors
   */
  private handleApiError(error: ApiError): void {
    switch (error.status) {
      case 401:
        this.handleUnauthorized();
        break;
      case 403:
        this.handleForbidden();
        break;
      case 404:
        this.handleNotFound();
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        this.handleServerError();
        break;
      default:
        this.handleGenericApiError(error);
    }
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: AuthError): void {
    switch (error.type) {
      case 'token_expired':
        this.handleTokenExpired();
        break;
      case 'token_invalid':
        this.handleTokenInvalid();
        break;
      case 'unauthorized':
        this.handleUnauthorized();
        break;
      case 'forbidden':
        this.handleForbidden();
        break;
    }
  }

  /**
   * Handle validation errors
   */
  private handleValidationError(error: ValidationError): void {
    // Show validation error to user
    this.showUserMessage('error', `Validation Error: ${error.message}`);
  }

  /**
   * Handle network errors
   */
  private handleNetworkError(error: NetworkError): void {
    // Only show user messages for critical network errors
    // Suppress routine API call failures to reduce console noise
    if (error.url && error.url.includes('/stats')) {
      // These are routine API calls that might fail - don't spam user
      return;
    }

    switch (error.type) {
      case 'timeout':
        this.showUserMessage('warning', 'Request timeout. Please try again.');
        break;
      case 'connection_failed':
        this.showUserMessage('error', 'Connection failed. Please check your internet connection.');
        break;
      case 'server_unavailable':
        this.showUserMessage('error', 'Server is currently unavailable. Please try again later.');
        break;
    }
  }

  /**
   * Handle business logic errors
   */
  private handleBusinessError(error: BusinessError): void {
    // Don't show user messages for routine API failures
    if (error.operation === 'api_call' && error.category === 'system') {
      // These are routine API calls that might fail - don't spam user
      return;
    }

    switch (error.severity) {
      case 'critical':
        this.showUserMessage('error', `Critical Error: ${error.message}`);
        break;
      case 'high':
        this.showUserMessage('error', `Error: ${error.message}`);
        break;
      case 'medium':
        this.showUserMessage('warning', `Warning: ${error.message}`);
        break;
      case 'low':
        this.showUserMessage('info', error.message);
        break;
    }
  }

  /**
   * Handle unknown errors
   */
  private handleUnknownError(error: AppError): void {
    this.showUserMessage('error', 'An unexpected error occurred. Please try again.');
  }

  // Specific error handlers
  private handleTokenExpired(): void {
    localStorage.clear();
    window.location.href = '/';
  }

  private handleTokenInvalid(): void {
    localStorage.clear();
    window.location.href = '/';
  }

  private handleUnauthorized(): void {
    localStorage.clear();
    window.location.href = '/';
  }

  private handleForbidden(): void {
    this.showUserMessage('error', 'You do not have permission to perform this action.');
  }

  private handleNotFound(): void {
    this.showUserMessage('error', 'The requested resource was not found.');
  }

  private handleServerError(): void {
    this.showUserMessage('error', 'Server error occurred. Please try again later.');
  }

  private handleGenericApiError(error: ApiError): void {
    this.showUserMessage('error', `API Error: ${error.message}`);
  }

  /**
   * Show message to user (placeholder)
   */
  private showUserMessage(type: 'info' | 'warning' | 'error', message: string): void {
    // Log all messages to console instead of showing browser alerts
    console.log(`User Message [${type}]: ${message}`);
    
    // For errors, also log to error console for debugging
    if (type === 'error') {
      console.error(`Error Message: ${message}`);
    }
  }

  // Type guards
  private isApiError(error: AppError): error is ApiError {
    return 'status' in error && 'endpoint' in error;
  }

  private isAuthError(error: AppError): error is AuthError {
    return 'type' in error && ['token_expired', 'token_invalid', 'unauthorized', 'forbidden'].includes(error.type);
  }

  private isValidationError(error: AppError): error is ValidationError {
    return 'field' in error && 'rule' in error;
  }

  private isNetworkError(error: AppError): error is NetworkError {
    return 'type' in error && ['timeout', 'connection_failed', 'server_unavailable'].includes(error.type);
  }

  private isBusinessError(error: AppError): error is BusinessError {
    return 'category' in error && 'operation' in error && 'severity' in error;
  }
}

// Utility functions
export const createErrorContext = (additional?: Record<string, any>): ErrorContext => ({
  timestamp: new Date(),
  userAgent: navigator.userAgent,
  url: window.location.href,
  additional
});

export const formatErrorForUser = (error: AppError): string => {
  // Return user-friendly error messages
  switch (error.code) {
    case ErrorCodes.TOKEN_EXPIRED:
      return 'Your session has expired. Please log in again.';
    case ErrorCodes.TOKEN_INVALID:
      return 'Invalid session. Please log in again.';
    case ErrorCodes.UNAUTHORIZED:
      return 'You are not authorized to perform this action.';
    case ErrorCodes.FORBIDDEN:
      return 'You do not have permission to access this resource.';
    case ErrorCodes.API_TIMEOUT:
      return 'Request timed out. Please try again.';
    case ErrorCodes.API_NETWORK_ERROR:
      return 'Network error. Please check your connection.';
    case ErrorCodes.VALIDATION_ERROR:
      return error.message;
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

export const isRetryableError = (error: AppError): boolean => {
  if (isNetworkError(error)) {
    return error.retryable;
  }
  
  if (isApiError(error)) {
    return error.status >= 500 || error.status === 408;
  }
  
  return false;
};

// Type guard functions
const isNetworkError = (error: AppError): error is NetworkError => {
  return 'type' in error && ['timeout', 'connection_failed', 'server_unavailable'].includes(error.type);
};

const isApiError = (error: AppError): error is ApiError => {
  return 'status' in error && 'endpoint' in error;
};

// Export singleton instances
export const errorLogger = ErrorLogger.getInstance();
export const errorHandler = ErrorHandler.getInstance();
